"use client";

import React, { useState, useCallback, useMemo } from "react";
import { useParams, usePathname } from "next/navigation";
import { cn } from "utils";
import { useSnapshot } from "valtio";
import log from "logbeacon";
import type { ChatRefContent } from "../../Sender/type";
import { useHomePageSenderContext } from "../HomePageSenderContext";
import { setCopilotSenderChatRefContent } from "../../CoPilot/CopilotAgent/CopilotSender";
import { MarketSummaryItem } from "./MarketSummaryItem";
import type { MarketSummaryItemData, MarketSummaryProps } from "./types";
import { SectionHeader } from "@/src/components/SectionHeader";
import {
  type StockType,
  getStockTypeFromPathname,
  DEFAULT_STOCK_TYPE,
} from "@/src/utils/stockType";
import Translations from "@translate/components/Home/MarketSummary/MarketSummary";
import { I18nUtil } from "@utils/i18n";
import { useMarketSummary } from "@/src/hooks";
import { rootStore } from "@/src/store";
import { ROUTER_PATH } from "@/src/config/router";
import { buildLocale } from "@/src/utils/buildLocale";
import { getRandomElements } from "@/src/utils";
import { useStartObserveHeight } from "@/src/mobiles/mainContainer/HeightRecorderContext";

const I18n = I18nUtil.createScoped(Translations);

/**
 * MarketSummary 组件
 * 市场摘要组件，支持 PC 和 H5 两种显示模式
 * 纯客户端渲染组件，数据通过客户端 hook 获取
 */
export const MarketSummary: React.FC<MarketSummaryProps> = ({
  items,
  stockType: propsStockType,
}) => {
  // H5 版本：管理每个卡片的展开状态
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const { type } = useSnapshot(rootStore.home);
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const params = useParams();
  const pathname = usePathname();
  const locale = (params?.locale as string) || "en";
  // 优先使用传入的 stockType，fallback 到从 pathname 解析，最后使用默认值
  const stockType: StockType =
    propsStockType ?? getStockTypeFromPathname(pathname) ?? DEFAULT_STOCK_TYPE;

  // 构建 Copilot 页面链接：已登录跳转到 CIO，未登录跳转到引导页
  const cioHref = useMemo(() => {
    const urlPrefix = buildLocale(locale);
    if (isLoggedIn) {
      return `${urlPrefix}${ROUTER_PATH.CO_PILOT}/cio`;
    }
    return `${urlPrefix}${ROUTER_PATH.CO_PILOT}`;
  }, [locale, isLoggedIn]);

  // 客户端获取数据，不使用 SSR 初始数据
  const { data: marketSummaryData, isLoading } = useMarketSummary({
    enabled: true,
    type,
    language: locale,
    stockType,
  });

  useStartObserveHeight(isLoading, !!marketSummaryData?.length);

  // 使用真实数据，如果没有数据或正在加载，则使用传入的 items 或默认数据
  const displayItems = useMemo(() => {
    if (items) {
      return items;
    }
    if (marketSummaryData && marketSummaryData.length > 0) {
      return marketSummaryData;
    }
    return [];
  }, [items, marketSummaryData]);

  // 获取 Context 中的 setChatRefContent 函数
  const { setChatRefContent } = useHomePageSenderContext();

  // 切换展开/收起状态
  const handleToggle = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // 默认 prompt 列表
  const defaultPrompts = useMemo(
    () => [
      I18n.t("What should I focus on in this news?"),
      I18n.t("How should I interpret this news for my portfolio?"),
      I18n.t("What are the key signals hidden behind this news?"),
      I18n.t("How does this event affect stocks and crypto today?"),
      I18n.t("What's the most important thing I should take from this?"),
      I18n.t("Is this a short-term reaction or a longer trend?"),
      I18n.t("What are the upside and downside cases from this?"),
    ],
    [],
  );

  // 处理 Ask 点击
  const handleAskClick = useCallback(
    (item: MarketSummaryItemData) => {
      const promptList = item?.questions?.length
        ? item?.questions
        : defaultPrompts;
      // 随机选择一条 prompt
      const randomPrompt = getRandomElements(promptList, 1)[0];
      const chatRefContent: ChatRefContent = {
        title: item.title,
        question: randomPrompt,
        desc: item.description,
        type: "MARKET_SUMMARY",
        refId: item.id,
        link: "",
        autoSubmit: true,
      };
      log.info(
        `[onChartClick][trace][${item?.questions?.length ? "quotes" : "default"}]`,
        randomPrompt,
        item.id,
      );
      setChatRefContent(chatRefContent);
      setCopilotSenderChatRefContent(chatRefContent);
    },
    [setChatRefContent, defaultPrompts],
  );

  const IsLoadingComponent = () => {
    return (
      <div className="flex flex-col items-start w-full overflow-hidden gap-4 md:bg-background_white_overlay5 md:rounded-3 md:gap-0">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="flex flex-col animate-pulse md:p-4 gap-3 w-full"
          >
            <div className="flex-1 flex flex-col gap-2">
              <div className="w-full h-4 rounded bg-background_white_overlay5" />
              <div className="h-4 w-[50%] rounded bg-background_white_overlay5" />
            </div>
            <div className="flex gap-2">
              <div className="w-4 h-4 rounded-full bg-background_white_overlay5" />
              <div className="h-4 w-[58px] rounded bg-background_white_overlay5" />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={cn(
        "flex flex-col items-start w-full",
        "px-4 pb-6 border-b border-l-0 border-r-0 border-t-0 border-border_white_transparent10",
        "md:px-0 md:pb-0 md:border-b-0 md:mt-0",
      )}
    >
      {/* 标题 */}
      {/* <p
        className={cn(
          "font-bold text-content_primary",
          isPc ? "headline_regular_m mt-4 mb-4" : "headline_regular_s mt-8 mb-6",
        )}
      >
        Market Summary
      </p> */}
      <SectionHeader
        title={I18n.t("Market Summary")}
        href={cioHref}
        showChevron={true}
      />
      {isLoading ? (
        <IsLoadingComponent />
      ) : (
        <div
          className={cn(
            "flex flex-col items-start w-full overflow-hidden",
            "gap-4",
            "md:bg-background_white_overlay5 md:rounded-3 md:gap-0",
          )}
        >
          {displayItems.map((item, index) => (
            <React.Fragment key={`${stockType}-${item.id}`}>
              <MarketSummaryItem
                item={item}
                isExpanded={expandedItems.has(item.id)}
                onToggle={() => handleToggle(item.id)}
                isFirst={index === 0}
                isLast={index === displayItems.length - 1}
                onAskClick={handleAskClick}
              />
              {/* 卡片之间的分隔线（仅移动端显示） */}
              {index < displayItems.length - 1 && (
                <div className="h-0 relative shrink-0 w-full md:hidden">
                  <div className="absolute bottom-[-0.5px] left-0 right-0 top-[-0.5px] border-t border-border_white_transparent10" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
      {/* 卡片列表 */}
    </div>
  );
};
